// Email templates go to the backend as both `html_content` (what gets sent)
// and `content`. For anything built in the Email Editor, `content` is the
// Unlayer design JSON, so a saved template reopens as real, draggable
// blocks (text, images, columns) instead of one locked HTML block. Emails
// written in the Plain Text tab have no design, so `content` is their text.

export type EmailDesign = { body: { rows: unknown[] } } & Record<string, unknown>;

export interface SavedTemplateVariable {
  name?: string;
  index?: number;
  example?: string;
}

// The backend rewrites `{{first_name}}` to its index (`{{2}}`) in `content`
// and returns the index → name map in `variables`. Put the names back.
export const restoreVariableNames = (text: string, variables?: SavedTemplateVariable[] | null): string => {
  if (!text || !variables?.length) return text;
  return text.replace(/\{\{\s*(\d+)\s*\}\}/g, (tag, n) => {
    const v = variables.find((x) => String(x.index) === n);
    return v?.name ? `{{${v.name}}}` : tag;
  });
};

// `content` -> Unlayer design, or null when it's plain text (Plain Text tab
// emails, and every template saved before this change).
export const parseEmailDesign = (content?: string | null, variables?: SavedTemplateVariable[] | null): EmailDesign | null => {
  const raw = content?.trim();
  if (!raw || raw[0] !== "{") return null;
  try {
    const design = JSON.parse(restoreVariableNames(raw, variables));
    return Array.isArray(design?.body?.rows) ? design : null;
  } catch {
    return null;
  }
};

// The saved `variables` -> the editor's "Replace with" rows. The backend
// echoes the name as the example when no default was given — that's blank.
export const defaultsFromSavedVariables = (variables?: SavedTemplateVariable[] | null) =>
  (variables || [])
    .filter((v) => v.name)
    .map((v) => ({ name: v.name as string, defaultValue: v.example && v.example !== v.name ? v.example : "" }));

// Fallback for templates with no design: the whole HTML as one block.
export const htmlBlockDesign = (html: string) => ({
  counters: { u_column: 1, u_row: 1, u_content_html: 1 },
  body: {
    rows: [{
      cells: [1],
      columns: [{
        contents: [{ type: "html", values: { html, containerPadding: "20px" } }],
        values: { backgroundColor: "", padding: "0px" },
      }],
      values: { padding: "0px" },
    }],
    values: { backgroundColor: "#ffffff", contentWidth: "600px" },
  },
  schemaVersion: 6,
});

// Email HTML (Plain Text markup or a full Unlayer document) -> readable text:
// line breaks at <br> and block ends, no <style>/<head> contents.
export const htmlToPlainText = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("style, script, head, title").forEach((el) => el.remove());
  doc.querySelectorAll("br").forEach((el) => el.replaceWith("\n"));
  doc.querySelectorAll("p, div, li, tr, h1, h2, h3, h4, h5, h6, table").forEach((el) => el.append("\n"));
  return (doc.body.textContent || "")
    .replace(/ /g, " ")
    .replace(/[ \t]+/g, " ")
    .replace(/ *\n */g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

// ── Older templates: rebuild blocks from the HTML ───────────────────────────
// Templates saved before `content` carried the design only have their
// exported HTML. Unlayer's export is regular — .u-row-container rows, .u-col
// columns, one presentation <table> per content block — so it maps back to
// real heading / text / image / button / divider blocks. Anything not
// recognised stays an HTML block, so nothing is lost.

const styleOf = (el: Element | null, prop: string) => (el as HTMLElement | null)?.style?.getPropertyValue(prop) || "";

// Unlayer's own forms want "140%" and "#rrggbb", not "1.4" / "rgb(...)".
const asPercent = (v: string) => (/^\d*\.?\d+$/.test(v) ? `${Math.round(Number(v) * 100)}%` : v);
const asHex = (v: string) => {
  const m = /^rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(v);
  return m ? "#" + m.slice(1, 4).map((n) => Number(n).toString(16).padStart(2, "0")).join("") : v;
};

const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);

const contentFromTable = (table: Element): Record<string, unknown> => {
  const td = table.querySelector("td");
  if (!td) return { type: "html", values: { html: table.outerHTML } };
  const containerPadding = styleOf(td, "padding") || "10px";

  const heading = td.querySelector("h1, h2, h3, h4");
  if (heading) {
    return {
      type: "heading",
      values: {
        containerPadding,
        headingType: heading.tagName.toLowerCase(),
        fontSize: styleOf(heading, "font-size") || "22px",
        textAlign: styleOf(heading, "text-align") || "left",
        lineHeight: asPercent(styleOf(heading, "line-height")) || "140%",
        text: heading.innerHTML.trim(),
      },
    };
  }

  const img = td.querySelector("img");
  if (img && !td.textContent?.trim()) {
    const link = img.closest("a");
    return {
      type: "image",
      values: {
        containerPadding,
        src: { url: img.getAttribute("src") || "", width: Number(img.getAttribute("width")) || undefined, height: Number(img.getAttribute("height")) || undefined },
        textAlign: img.closest("td[align]")?.getAttribute("align") || "center",
        altText: img.getAttribute("alt") || "",
        action: { name: "web", values: { href: link?.getAttribute("href") || "", target: link?.getAttribute("target") || "_blank" } },
      },
    };
  }

  const button = td.querySelector("a[style*='background']");
  if (button) {
    return {
      type: "button",
      values: {
        containerPadding,
        text: button.innerHTML.trim(),
        href: { name: "web", values: { href: button.getAttribute("href") || "", target: button.getAttribute("target") || "_blank" } },
        buttonColors: { color: asHex(styleOf(button, "color")) || "#FFFFFF", backgroundColor: asHex(styleOf(button, "background-color")) || "#3AAEE0" },
        textAlign: button.closest("[align]")?.getAttribute("align") || "center",
      },
    };
  }

  if (!td.textContent?.trim() && td.querySelector("table[style*='border-top']")) {
    return { type: "divider", values: { containerPadding } };
  }

  const textDiv = Array.from(td.children).find((c) => c.tagName === "DIV");
  if (textDiv && Array.from(td.children).filter((c) => c.tagName !== "STYLE").length === 1) {
    return {
      type: "text",
      values: {
        containerPadding,
        textAlign: styleOf(textDiv, "text-align") || "left",
        lineHeight: asPercent(styleOf(textDiv, "line-height")) || "140%",
        text: textDiv.innerHTML.trim(),
      },
    };
  }

  return { type: "html", values: { containerPadding, html: td.innerHTML.trim() } };
};

export const designFromHtml = (html: string): object | null => {
  if (!html.trim()) return null;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const rows = Array.from(doc.querySelectorAll(".u-row-container"));

  // Not an editor export (Plain Text markup, pasted HTML): one editable
  // text block rather than a locked HTML block.
  if (rows.length === 0) {
    if (/^\s*(<!doctype|<html)/i.test(html)) return htmlBlockDesign(html);
    return {
      counters: { u_column: 1, u_row: 1, u_content_text: 1 },
      body: {
        rows: [{ cells: [1], columns: [{ contents: [{ type: "text", values: { containerPadding: "20px", text: html } }], values: {} }], values: {} }],
        values: { backgroundColor: "#ffffff", contentWidth: "600px" },
      },
      schemaVersion: 6,
    };
  }

  const designRows = rows.map((row) => {
    const cols = Array.from(row.querySelectorAll(".u-col"));
    const widths = cols.map((c) => Number(/u-col-(\d+)/.exec(c.className)?.[1]) || 100);
    const div = widths.reduce((a, b) => gcd(a, b));
    return {
      cells: widths.map((w) => w / div),
      columns: cols.map((col) => ({
        contents: Array.from(col.querySelectorAll(":scope > div > div > table")).map(contentFromTable),
        values: {},
      })),
      values: { backgroundColor: asHex(styleOf(row.querySelector(".u-row"), "background-color")) },
    };
  });

  return {
    counters: {},
    body: {
      rows: designRows,
      values: {
        backgroundColor: asHex(styleOf(doc.body, "background-color")) || "#ffffff",
        contentWidth: styleOf(doc.querySelector(".u-row"), "max-width") || "600px",
        fontFamily: { label: "Arial", value: "arial,helvetica,sans-serif" },
      },
    },
    schemaVersion: 6,
  };
};
